/** Phase D texts (life). Each package owns its own namespace; labels live in the sub-tables. */
export const enLife = {
  life: {
    mapNames: {} as Record<string, string>,
    zones: {
      fastFood: 'Burger Corner',
    },
    objects: {
      life_piggy: 'Piggy bank',
      life_fastfood: 'Burger Corner',
      life_adscreen: 'Ad screen',
    },
    npc: {} as Record<string, string>,
    items: {
      umbrella: { name: 'Umbrella', desc: 'Keeps you dry when it rains. A need on rainy days!' },
    } as Record<string, { name: string; desc: string }>,

    need: 'Need',
    want: 'Want',
    mom: 'Mom',
    coins: (n: number) => `${n}🪙`,

    allowance: {
      title: '💰 Weekly allowance',
      text: 'Good morning! Here’s your allowance for the week: **100 coins**.\nHow will you split it? Some to spend, some for the piggy bank, and some for gifts.',
      split: 'Let’s plan it',
      hint: 'Tap − and + to move coins. It must add up to 100.',
      spend: '🛍️ To spend',
      piggy: '🐷 Piggy bank',
      gift: '🎁 Gift fund',
      presets: { balanced: '⚖️ Balanced', saver: '🐷 Saver', spender: '🛍️ Spender' },
      left: (n: number) => (n === 0 ? '✅ All 100 coins have a job!' : n > 0 ? `${n} coins still need a job` : `${-n} coins too many`),
      confirm: 'That’s my plan',
      momSaver: 'Wow, you’re saving a lot! On Sundays I add 10 % to whatever is in your piggy bank. Watch it grow!',
      momBalanced: 'A nice balanced plan. Spend a little, save a little, share a little.',
      momSpender: 'That’s a lot to spend! It’s your choice — but remember, when it’s gone, it’s gone until next Saturday.',
    },

    interest: {
      title: '🐷 Sunday matching',
      text: (add: number, total: number) =>
        `Mom peeks into your piggy bank and adds **${add} coins** — that’s 10 % of what you saved.\nNow there are **${total} coins** inside!\n\nThis is how a bank pays “interest”: money you leave saved grows by itself.`,
      empty: 'Mom checks your piggy bank: it’s empty. “Every Sunday I add 10 % to what you’ve saved. Even 10 coins would grow!”',
    },

    piggy: {
      text: (piggy: number, coins: number) => `A pink pig with a coin slot. Inside: **${piggy}🪙**. In your pocket: **${coins}🪙**.`,
      in: (n: number) => `Put in ${n}🪙`,
      out: (n: number) => `Take out ${n}🪙`,
      all: 'Put everything in',
      done: (piggy: number) => `Clink! The piggy bank now holds ${piggy}🪙.`,
      outWarn: 'Taking money out is fine — just ask yourself: is it for a need or a want?',
      wallet: '👛 Open my wallet',
    },

    nw: {
      title: 'Quick question',
      ask: (item: string) => `Before you pay: is **${item}** a need or a want?`,
      right: (item: string, kind: string) => `Right! ${item} is a **${kind}**.`,
      wrong: (item: string, kind: string) => `Hmm, ${item} is usually a **${kind}**.`,
      needWhy: 'Needs keep you healthy, safe and ready for school.',
      wantWhy: 'Wants are nice extras. It’s OK to buy them — just plan for them first.',
      buy: 'Buy it',
    },

    ad: {
      sources: { tv: '📺 TV ad', mall: '🏬 Mall screen', phone: '📱 Phone pop-up' },
      flash: 'LIMITED OFFER!',
      only: 'ONLY TODAY!',
      was: (n: number) => `Was ${n}🪙`,
      now: (n: number) => `NOW ${n}🪙`,
      timer: (s: number) => `Offer ends in ${s}…`,
      buy: (n: number) => `Buy now (${n}🪙)`,
      wait: '⏳ Wait until tomorrow',
      boughtLesson: (was: number, now: number) =>
        `You bought it! But look again: the “was ${was}” price is made up, and ${now} is the normal price.\nAds use hurry words like **LIMITED** and **ONLY TODAY** so you buy before you think.`,
      waitLesson: 'Smart! Added to your wish list. A good trick: wait one day before buying a want. If you still want it tomorrow, you can still buy it.',
      poor: 'You don’t have enough coins — the ad still wants you to feel you need it!',
    },

    wish: {
      title: '📝 Your wish list',
      cheaper: (item: string, was: number, now: number) => `Remember the ${item} from the ad? Today it’s **${now}🪙** instead of ${was}🪙. The “limited” offer wasn’t so limited after all!`,
      forgot: (item: string) => `You wake up and think about the ${item} from the ad… you don’t really need it. You kept your coins!`,
      lesson: 'Waiting a day helps you tell a real need from a quick want.',
      buy: (n: number) => `Buy it now for ${n}🪙`,
      skip: 'No thanks, I’ll keep my coins',
      kept: 'Coins saved. Your future self says thanks!',
    },

    wallet: {
      title: '👛 My wallet',
      menu: '👛 Wallet',
      pocket: 'Pocket',
      piggy: 'Piggy bank',
      gift: 'Gift fund',
      budget: 'This week’s budget',
      spent: (s: number, b: number) => (b ? `${s} of ${b}🪙 spent` : `${s}🪙 spent (no plan yet — allowance comes on Saturday)`),
      over: 'Over budget! Try to wait until next week for wants.',
      donut: 'Needs vs wants this week',
      none: 'No spending yet this week.',
      allTime: (n: number, w: number) => `All time: ${n}🪙 on needs · ${w}🪙 on wants`,
      wishes: 'Wish list',
      noWishes: 'Empty — no impulse buys waiting.',
      quiz: (n: number) => `Need-or-want questions right: ${n}`,
      tip: '💡 Tip',
      tips: [
        'Pay yourself first: put coins in the piggy bank before you shop.',
        'A want isn’t bad — plan for it, and it feels even better when you buy it.',
        '“Was 40, now 25!” — the “was” price is often made up.',
        'Combo meals save money only if you want everything in the combo.',
        'Saving 10 coins a week is 520 coins in a year.',
        'Before buying, ask: Do I need it? Do I have room for it? Will I still want it next week?',
        'Compare the price of ONE: 3 for 45 is 15 each; 2 for 28 is 14 each.',
      ],
    },

    ws: {
      kinds: { homework: '📒 Homework', quiz: '📝 Quiz', exam: '🧪 Exam' },
      gradeLabel: (g: number) => `Grade ${g}`,
      progress: (i: number, n: number) => `Question ${i} of ${n}`,
      types: { spell: '🔤 Spelling', cloze: '✏️ Fill the gap', math: '🪙 Money maths', reading: '📖 Reading', logic: '🧩 Logic' },
      spellHint: 'Listen and tap the letters in order.',
      clozeHint: 'Choose the missing word.',
      mathHint: 'Work it out — use scrap paper if you like!',
      readingHint: 'Read the text, then answer.',
      logicHint: 'Find the pattern.',
      hear: '🔊 Hear it',
      undo: '⌫ Undo',
      next: 'Next ▶',
      finish: 'See my grade',
      right: '✅ Correct!',
      wrong: (a: string) => `❌ The answer is: ${a}`,
      result: (s: number, n: number) => `${s} / ${n} correct`,
      comments: { A: 'Excellent work! 🌟', B: 'Good job — a little more practice!', C: 'Keep trying — mistakes help you learn.' },
      done: 'Hand it in',
      math: {
        items: ['rice ball', 'juice', 'pencil', 'sticker', 'notebook', 'keychain'],
        left: (a: number, item: string, b: number) => `You have ${a} coins. You buy a ${item} for ${b} coins. How many coins are left?`,
        change: (n: number, item: string, p: number, pay: number) => `You buy ${n} × ${item} at ${p} coins each and pay with ${pay} coins. How much change do you get?`,
        discount: (item: string, p: number, d: number) => `A ${item} costs ${p} coins. Today it is ${d}% off. What is the new price?`,
        weeks: (x: number, item: string, y: number) => `You save ${x} coins every week. How many weeks until you can buy a ${item} for ${y} coins?`,
        unit: (item: string, nA: number, pA: number, nB: number, pB: number) =>
          `Pack A: ${nA} × ${item} for ${pA} coins. Pack B: ${nB} × ${item} for ${pB} coins. How much is ONE ${item} in the cheaper pack?`,
        interest: (p: number) => `You have ${p} coins in the piggy bank. Mom adds 10% every week (on the new total). How many coins after 2 weeks?`,
      },
      logic: (seq: string) => `What comes next?  ${seq}, ?`,
    },

    desk: {
      portfolio: '🖼️ My portfolio',
      worksheet: 'Your homework sheet is ready: five questions for your grade.',
    },

    exhibit: {
      title: '🖼️ Learning exhibition',
      titlePortfolio: '🖼️ My portfolio',
      today: 'It’s **exhibition day** (成果展)! Every fourth week the school shows what students made. Your worksheets, drawings and stamps are on the wall.',
      see: 'See my corner',
      works: 'Best worksheets',
      noWorks: 'No worksheets yet — do your homework at the study desk.',
      drawings: 'PixelBook drawings',
      noDrawings: 'No drawings yet — draw something on PixelBook in your phone.',
      stamps: 'Passport stamps',
      noStamps: 'No stamps yet — go on trips and celebrate festivals!',
      reward: (n: number) => `Visitors love your corner! +${n}⭐`,
      next: (w: number) => `Next exhibition: week ${w}`,
    },

    listen: {
      tab: '👂 Listen',
      pick: '👂 Listen & pick',
      dict: '⌨️ Dictation',
      pickHint: 'Listen, then tap what you heard.',
      dictHint: 'Listen and type what you hear.',
      play: '🔊 Play',
      slow: '🐢 Slow',
      check: 'Check',
      placeholder: 'Type here…',
      ok: 'Perfect!',
      close: (a: string) => `Almost! It was: “${a}”`,
      bad: (a: string) => `Not quite. It was: “${a}”`,
      next: 'Next ▶',
      streak: (n: number) => `🔥 Streak: ${n}`,
      shadow: '🎤 Say it',
    },

    replay: { again: '🔊', slow: '🐢', mic: '🎤', listening: 'Listening…', good: 'Great shadowing!', retry: (h: string) => `I heard “${h}”. Try again!` },

    grade: { title: '🎒 School grade', hint: 'Sets how hard homework and exams are.', names: ['3rd', '4th', '5th', '6th'] },

    ff: {
      title: '🍔 Burger Corner',
      hint: 'Order in English and compare prices!',
      menu: [
        { name: 'Cheeseburger', desc: 'Warm and filling', en: 'a cheeseburger' },
        { name: 'Fries', desc: 'Salty and crunchy', en: 'some fries' },
        { name: 'Cola', desc: 'Very sweet (lots of sugar!)', en: 'a cola' },
        { name: 'Combo meal', desc: 'Burger + fries + cola', en: 'the combo meal' },
        { name: '🥗 Veggie wrap + soy milk', desc: 'The healthy choice', en: 'a veggie wrap and a soy milk' },
      ],
      compare: '🧮 Combo or not?',
      compareText: (sum: number, combo: number) => `Burger + fries + cola one by one = **${sum}🪙**. The combo = **${combo}🪙**. You save ${sum - combo}🪙 — but only if you want all three!`,
      water: 'Free water? Just ask: “Can I have some water, please?”',
      cashier: 'Cashier',
      greet: 'Hi! Welcome to Burger Corner. What can I get for you?', // English practice
      prompt: 'Choose the polite way to order:',
      wrong: ['The cashier blinks. “Sorry? Could you say that politely?”', 'Hmm, that sounds a bit rude. Try “Can I have…, please?”'],
      done: 'Here you go! Enjoy your meal!', // English practice
      poor: 'Not enough coins. Maybe something cheaper?',
      healthy: 'A healthy choice! Your body says thank you. +2⭐',
      tooMuch: 'That was a lot of salt and sugar… you feel a bit heavy.',
      look: 'Just looking, thanks',
      waterDone: 'Sure! Here’s a cup of water.', // English practice
    },

    save: {
      menu: '🔑 Save code',
      title: '🔑 Save code',
      exportHint: 'Copy this code to keep your game safe or move it to another device.',
      copy: '📋 Copy code',
      copied: 'Copied!',
      importHint: 'Paste a save code here to load that game.',
      placeholder: 'Paste a code…',
      load: '📥 Load this code',
      bad: 'That code doesn’t work. Check you copied all of it.',
      confirm: 'Load this save? Your current game will be replaced.',
      startButton: '🔑 Load a save code',
    },

    journal: {
      allowance: (split: string) => `Planned my allowance: ${split}`,
      exhibit: 'Showed my work at the exhibition',
      waited: 'Waited a day instead of buying on impulse',
      worksheet: (g: string) => `Got ${g} on a worksheet`,
    },
  },
};
